import { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react'
import { FaStar, FaStarHalfAlt, FaQuoteLeft } from 'react-icons/fa'
import { getTestimonials } from '../utils/api'
import VariableProximity from '../components/VariableProximity'

// Fallback testimonials data
const fallbackTestimonials = [
  {
    name: 'Elite Fresh Logistics',
    role: 'Company',
    company: 'Elite Fresh Logistics',
    rating: 5,
    text: 'We tried two other dispatch services before this one. The difference was immediate. They keep our reefer trucks loaded consistently and respond in minutes, not hours. Revenue has noticeably increased.',
    image: 'EF'
  },
  {
    name: 'Rashid K.',
    role: 'Owner-Operator',
    company: 'O/O',
    rating: 4.5,
    text: 'As a new owner-operator, I was nervous about finding loads. Their team walked me through everything—from setting up with brokers to managing rate cons. Could not have asked for a better dispatch partner.',
    image: 'RK'
  },
  {
    name: 'Silver Line Freight LLC',
    role: 'Company',
    company: 'Silver Line Freight LLC',
    rating: 5,
    text: 'The customer service is exceptional. They treat our trucks like their own—always transparent, always professional. Our drivers appreciate how smooth every load is.',
    image: 'SL'
  },
  {
    name: 'Bryan',
    role: 'Flatbed Driver',
    company: '',
    rating: 4,
    text: 'Reliable, honest and always finds top-paying loads.',
    image: 'BR'
  },
  {
    name: 'Midwest Freight Co.',
    role: 'Company',
    company: 'Midwest Freight Co.',
    rating: 4.5,
    text: 'Best dispatch experience we have had in years.',
    image: 'MF'
  },
  {
    name: 'Karen',
    role: 'Hotshot Driver',
    company: '',
    rating: 5,
    text: 'They saved me hours every week by handling paperwork and negotiations.',
    image: 'KA'
  },
  {
    name: 'ProHaul Transport',
    role: 'Company',
    company: 'ProHaul Transport',
    rating: 5,
    text: 'Our weekly revenue increased by 18% within the first 45 days. Their rate negotiation is next level.',
    image: 'PH'
  },
  {
    name: 'David L.',
    role: 'Fleet Owner',
    company: '',
    rating: 4,
    text: 'Deadhead reduced significantly. They plan routes like pros.',
    image: 'DL'
  },
  {
    name: 'Tariq S.',
    role: 'Owner-Operator',
    company: '',
    rating: 4.5,
    text: 'Loads are consistent, payments are on time, and support is available 24/7. Exactly what a driver needs.',
    image: 'TS'
  }
]

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState(fallbackTestimonials)
  const [isLoading, setIsLoading] = useState(true)
  const testimonialsHeadingRef = useRef(null)
  const carouselRef = useRef(null)
  const scrollContainerRef = useRef(null)
  const isScrollingRef = useRef(false)
  const autoScrollIntervalRef = useRef(null)
  const resumeTimeout = useRef(null)
  const rafId = useRef(null)

  useEffect(() => {
    // Always use fallback testimonials for now
    setTestimonials(fallbackTestimonials)
    setIsLoading(false)
  }, [])

  useEffect(() => {
    // Auto-scroll functionality
    const startAutoScroll = () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current)
      }
      
      autoScrollIntervalRef.current = setInterval(() => {
        if (!scrollContainerRef.current || isScrollingRef.current) return
        
        const container = scrollContainerRef.current
        const scrollWidth = container.scrollWidth
        const clientWidth = container.clientWidth
        const currentScroll = container.scrollLeft
        
        // If we're at the end, scroll back to start
        if (currentScroll + clientWidth >= scrollWidth - 10) {
          requestAnimationFrame(() => {
            container.scrollTo({ left: 0, behavior: 'smooth' })
          })
        } else {
          // Scroll to next card
          const cardWidth = container.querySelector('.testimonial-card')?.offsetWidth || 0
          const gap = 20 // gap-5 = 1.25rem = 20px
          const scrollAmount = cardWidth + gap
          requestAnimationFrame(() => {
            container.scrollBy({ left: scrollAmount, behavior: 'smooth' })
          })
        }
      }, 4000) // Auto-scroll every 4 seconds
    }
    
    startAutoScroll()
    
    return () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current)
      }
      if (resumeTimeout.current) {
        clearTimeout(resumeTimeout.current)
      }
      if (rafId.current) {
        cancelAnimationFrame(rafId.current)
      }
    }
  }, [testimonials])

  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return
    
    // Cancel any pending animation frame
    if (rafId.current) {
      cancelAnimationFrame(rafId.current)
    }
    
    // Use requestAnimationFrame to throttle scroll handling
    rafId.current = requestAnimationFrame(() => {
      isScrollingRef.current = true
      if (resumeTimeout.current) {
        clearTimeout(resumeTimeout.current)
      }
      // Reset scrolling flag after scroll ends
      resumeTimeout.current = setTimeout(() => {
        isScrollingRef.current = false
      }, 300)
    })
  }, [])

  const handleWheel = useCallback((e) => {
    if (!scrollContainerRef.current) return
    
    const container = scrollContainerRef.current
    const deltaX = e.deltaX
    const deltaY = e.deltaY
    const isHorizontalScroll = Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 5
    
    // Only handle horizontal scrolling
    if (isHorizontalScroll) {
      e.preventDefault()
      e.stopPropagation()
      
      // Cancel any pending animation frame
      if (rafId.current) {
        cancelAnimationFrame(rafId.current)
      }
      
      // Use requestAnimationFrame for smooth scrolling with better performance
      rafId.current = requestAnimationFrame(() => {
        // Use native smooth scrolling behavior
        container.scrollBy({ 
          left: deltaX, 
          behavior: 'auto' // Use 'auto' for immediate response, browser handles smoothness
        })
        
        isScrollingRef.current = true
        if (resumeTimeout.current) {
          clearTimeout(resumeTimeout.current)
        }
        resumeTimeout.current = setTimeout(() => {
          isScrollingRef.current = false
        }, 300)
      })
    }
  }, [])

  const handleMouseEnter = useCallback(() => {
    // Pause auto-scroll when hovering
    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current)
      autoScrollIntervalRef.current = null
    }
  }, [])

  const handleMouseLeave = useCallback(() => {
    // Resume auto-scroll after leaving
    if (resumeTimeout.current) {
      clearTimeout(resumeTimeout.current)
    }
      resumeTimeout.current = setTimeout(() => {
        // Restart auto-scroll
        if (autoScrollIntervalRef.current) {
          clearInterval(autoScrollIntervalRef.current)
        }
        const startAutoScroll = () => {
          if (!scrollContainerRef.current || isScrollingRef.current) return
          
          const container = scrollContainerRef.current
          const scrollWidth = container.scrollWidth
          const clientWidth = container.clientWidth
          const currentScroll = container.scrollLeft
          
          if (currentScroll + clientWidth >= scrollWidth - 10) {
            requestAnimationFrame(() => {
              container.scrollTo({ left: 0, behavior: 'smooth' })
            })
          } else {
            const cardWidth = container.querySelector('.testimonial-card')?.offsetWidth || 0
            const gap = 20
            const scrollAmount = cardWidth + gap
            requestAnimationFrame(() => {
              container.scrollBy({ left: scrollAmount, behavior: 'smooth' })
            })
          }
        }
        
        autoScrollIntervalRef.current = setInterval(startAutoScroll, 4000)
      }, 2000)
  }, [])

  const StarRating = memo(({ rating }) => {
    const fullStars = Math.floor(rating)
    const hasHalfStar = rating % 1 !== 0
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0)
    
    return (
      <div className="flex items-center space-x-0.5">
        {[...Array(fullStars)].map((_, i) => (
          <FaStar
            key={`full-${i}`}
            className="text-amber-400 drop-shadow-[0_0_3px_rgba(251,191,36,0.6)]"
            size={12}
          />
        ))}
        {hasHalfStar && (
          <FaStarHalfAlt
            className="text-amber-400 drop-shadow-[0_0_3px_rgba(251,191,36,0.6)]"
            size={12}
          />
        )}
        {[...Array(emptyStars)].map((_, i) => (
          <FaStar
            key={`empty-${i}`}
            className="text-white/30"
            size={12}
          />
        ))}
      </div>
    )
  })

  if (isLoading) {
    return (
      <div className="section-padding text-center pt-24" style={{ backgroundColor: 'rgba(0,0,0,0.76)' }}>
        <div className="text-white text-xl">Loading testimonials...</div>
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <section className="section-padding text-white pt-24 border-b border-dark-700/30 bg-transparent">
        <div className="container-custom text-center">
          <div
            ref={testimonialsHeadingRef}
            style={{ position: 'relative' }}
            className="mb-4"
          >
            <VariableProximity
              label="What Our Clients Say"
              className="text-5xl font-bold"
              fromFontVariationSettings="'wght' 400, 'opsz' 9"
              toFontVariationSettings="'wght' 1000, 'opsz' 40"
              containerRef={testimonialsHeadingRef}
              radius={100}
              falloff="linear"
              style={{ color: 'white' }}
            />
          </div>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Do not just take our word for it. Hear from successful carriers who trust EW Logistics & IT Solution LLC 
            for their freight consultancy needs.
          </p>
        </div>
      </section>

      {/* Testimonials Carousel - Smooth Horizontal Scroll */}
      <section
        className="testimonials-section relative overflow-y-visible bg-transparent flex flex-col justify-center items-center pt-20 md:pt-24 pb-8"
        style={{ overflowY: 'visible' }}
      >
        <div
          ref={carouselRef}
          className="w-full max-w-6xl mx-auto px-3 lg:px-4 relative"
        >
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            onWheel={handleWheel}
            className="flex gap-4 lg:gap-5 overflow-x-auto overflow-y-visible scrollbar-hide"
            style={{
              scrollBehavior: 'smooth',
              scrollSnapType: 'x proximity',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              willChange: 'scroll-position',
            }}
          >
            {testimonials.map((testimonial, idx) => (
              <div
                key={idx}
                className="testimonial-card relative z-10 flex-shrink-0 w-[calc(100%-1rem)] md:w-[calc(50%-0.625rem)] lg:w-[calc(33.333%-1.25rem)] min-h-[260px] rounded-xl border border-[#00ffc3]/60 outline outline-1 outline-[#00e0ff]/45 outline-offset-[3px] bg-white/10 text-white shadow-[0_15px_35px_-18px_rgba(0,0,0,0.55),0_0_30px_rgba(0,224,255,0.2)] backdrop-blur-xl p-5 flex flex-col justify-between transition-all duration-300 ease-in-out hover:scale-110 hover:-translate-y-4 hover:shadow-[0_30px_60px_-15px_rgba(0,0,0,0.9),0_0_60px_rgba(0,255,195,0.8),0_0_100px_rgba(0,224,255,0.5),inset_0_0_20px_rgba(0,255,195,0.2)] hover:border-[#00ffc3] hover:border-2 hover:outline-[#00e0ff] hover:outline-2 hover:outline-offset-[4px] hover:bg-white/20 hover:backdrop-blur-2xl cursor-pointer"
                style={{
                  scrollSnapAlign: 'start',
                  transform: 'translateZ(0)',
                  willChange: 'auto',
                  backfaceVisibility: 'hidden',
                  contain: 'layout style paint'
                }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <div>
                  <div className="flex items-center mb-2">
                    <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-white mr-3">
                      <span className="block w-full text-center text-base font-bold leading-none tracking-tight">
                        {testimonial.image || 'U'}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-white">
                        {testimonial.name}
                      </h3>
                      <p className="text-white/90 text-xs">
                        {testimonial.role} {testimonial.company ? `• ${testimonial.company}` : ''}
                      </p>
                    </div>
                  </div>
                  <StarRating rating={testimonial.rating} />
                </div>
                <div className="mt-2 relative">
                  <FaQuoteLeft className="text-white/70 text-2xl mb-1" />
                  <p className="text-white/95 text-sm leading-relaxed">
                    {testimonial.text}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}

export default Testimonials

